/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { DineoutCartContext, ICharge, useSessionStorage } from "@swiggy-private/checkout-core";
import { CollapsableBillDetailsVariants, RenderingDetails } from "@swiggy-private/cart-types";
import { BILL_ELEMENT_ENTRIES } from "@swiggy-private/dineout-payment-success-ui";
import type { IDiscount } from "@swiggy-private/checkout-types";

// helpers
import { format, isEmpty } from "@swiggy-private/js-utils";
import {
    getRenderingDetails,
    getRenderingSeprator,
    getRenderingSubDetails,
} from "@dineout-cart-client/helpers/StealDealCart";

// types
import { IBillDetailsRendererProps } from "@swiggy-private/dineout-cart-ui";

// hooks
import { useStealDealAnalytics } from "./useStealDealAnalytics";

// constants
import {
    COLLAPSIBLE_BILL_DISCOUNT_MESSAGE,
    SESSION_STORAGE_KEYS,
    STEAL_STORE_BILL_DETAILS_KEYS,
    STEAL_STORE_BILL_DETAILS_TYPES,
} from "../constants";

interface IUseDineoutCollapsibleBillDetailsReturn {
    billDetailsProps: IBillDetailsRendererProps;
}

const useDineoutCollapsibleBillDetails = (): IUseDineoutCollapsibleBillDetailsReturn => {
    const { cartState } = useContext(DineoutCartContext);
    const { reviewedCart } = cartState;

    const cartResponse = useMemo(() => reviewedCart?.data, [reviewedCart]);
    const {
        onImpressionBillViewChevron,
        onClickBillViewChevron,
        onImpressionCollapsibleBillUpsell,
        onImpressionDeliveryFeeLineItem,
        onImpressionTaxesAndChargesLineItem,
        onImpressionDeliveryFeeUnCollapsedLineItem,
        onImpressionBanner,
        onClickBanner,
        onImpressionBannerHalfcard,
        onClickCloseBannerHalfcard,
    } = useStealDealAnalytics(cartResponse);

    const createBillEntries = useCallback((): RenderingDetails[] => {
        const billEntries: RenderingDetails[] = [];
        const bill = cartResponse?.bill;
        if (!bill) {
            return billEntries;
        }
        const { discounts, charges, dineoutPricingInfo } = bill;

        // Process item total
        if (!isEmpty(dineoutPricingInfo)) {
            billEntries.push(
                getRenderingDetails({
                    type: STEAL_STORE_BILL_DETAILS_TYPES.DISPLAY,
                    key: STEAL_STORE_BILL_DETAILS_KEYS.ITEM_TOTAL,
                    displayText: BILL_ELEMENT_ENTRIES.ITEM_TOTAL,
                    value: String(dineoutPricingInfo?.billAmountToApplyCoupon ?? ""),
                }),
            );
            billEntries.push(getRenderingSeprator());
        }

        // Process discounts
        if (!isEmpty(discounts)) {
            discounts.forEach((discount: IDiscount) => {
                const subDetails: RenderingDetails["sub_details"] = [];
                let isTypeInfoDisplay = false;
                if (isEmpty(discount?.value)) return;

                if (!isEmpty(discount?.ctx?.displayName)) {
                    subDetails.push(
                        getRenderingSubDetails({
                            type: STEAL_STORE_BILL_DETAILS_TYPES.TITLE,
                            key: STEAL_STORE_BILL_DETAILS_KEYS.DISCOUNT_DISPLAY_NAME,
                            displayText: discount?.ctx?.displayName,
                        }),
                    );
                }
                if (!isEmpty(discount?.ctx?.description)) {
                    subDetails.push(
                        getRenderingSubDetails({
                            type: STEAL_STORE_BILL_DETAILS_TYPES.INFODISPLAY,
                            key: STEAL_STORE_BILL_DETAILS_KEYS.DISCOUNT_DESCRIPTION,
                            displayText: discount?.ctx?.description,
                        }),
                    );
                    isTypeInfoDisplay = true;
                }
                billEntries.push(
                    getRenderingDetails({
                        type: isTypeInfoDisplay
                            ? STEAL_STORE_BILL_DETAILS_TYPES.TOOLTIPDISPLAY
                            : STEAL_STORE_BILL_DETAILS_TYPES.DISPLAY,
                        key: STEAL_STORE_BILL_DETAILS_KEYS.DISCOUNT_FEE,
                        displayText: discount?.ctx?.displayName || BILL_ELEMENT_ENTRIES.OFFER_AND_DISCOUNT,
                        value: String(discount.value),
                        sub_details: subDetails,
                        is_negative: 1,
                    }),
                );
            });
        }

        // Process charges
        if (!isEmpty(charges)) {
            charges.forEach((charge: ICharge) => {
                const subDetails: RenderingDetails["sub_details"] = [];
                let isTypeInfoDisplay = false;

                if (isEmpty(charge?.value)) return;

                if (!isEmpty(charge?.ctx?.displayName)) {
                    subDetails.push(
                        getRenderingSubDetails({
                            type: STEAL_STORE_BILL_DETAILS_TYPES.TITLE,
                            key: STEAL_STORE_BILL_DETAILS_KEYS.CHARGE_DISPLAY_NAME,
                            displayText: charge?.ctx?.displayName,
                        }),
                    );
                }
                if (!isEmpty(charge?.ctx?.description)) {
                    subDetails.push(
                        getRenderingSubDetails({
                            type: STEAL_STORE_BILL_DETAILS_TYPES.INFODISPLAY,
                            key: STEAL_STORE_BILL_DETAILS_KEYS.CHARGE_DESCRIPTION,
                            displayText: charge?.ctx?.description,
                        }),
                    );
                    isTypeInfoDisplay = true;
                }
                billEntries.push(
                    getRenderingDetails({
                        type: isTypeInfoDisplay
                            ? STEAL_STORE_BILL_DETAILS_TYPES.TOOLTIPDISPLAY
                            : STEAL_STORE_BILL_DETAILS_TYPES.DISPLAY,
                        key: STEAL_STORE_BILL_DETAILS_KEYS.CHARGE_FEE,
                        displayText: charge?.ctx?.displayName || BILL_ELEMENT_ENTRIES.CONVENIENCE_FEE,
                        value: String(charge?.value),
                        sub_details: subDetails,
                    }),
                );
                billEntries.push(getRenderingSeprator());
            });
        }

        // process GST
        if (!isEmpty(dineoutPricingInfo?.gst)) {
            const gstConvenienceFee = dineoutPricingInfo?.gst[0];
            billEntries.push(
                getRenderingDetails({
                    type: STEAL_STORE_BILL_DETAILS_TYPES.DISPLAY,
                    key: STEAL_STORE_BILL_DETAILS_KEYS.GST_FEE,
                    displayText: BILL_ELEMENT_ENTRIES.GST_CONVENIENCE_FEE,
                    value: String(gstConvenienceFee.totalGST),
                }),
            );
            billEntries.push(getRenderingSeprator());
        }

        // Process to pay
        billEntries.push(
            getRenderingDetails({
                type: STEAL_STORE_BILL_DETAILS_TYPES.DISPLAY,
                key: STEAL_STORE_BILL_DETAILS_KEYS.GRAND_TOTAL,
                displayText: BILL_ELEMENT_ENTRIES.TO_PAY,
                value: String(dineoutPricingInfo?.billToPay),
                meta: {
                    bold: true,
                    align: "right",
                },
            }),
        );

        billEntries.push(
            getRenderingDetails({
                type: STEAL_STORE_BILL_DETAILS_TYPES.BILL_DETAILS_SECTION_END,
                key: STEAL_STORE_BILL_DETAILS_KEYS.BILL_DETAILS_SECTION_END,
                displayText: "",
                value: "",
            }),
        );

        return billEntries;
    }, [cartResponse]);

    const renderingDetails = createBillEntries();

    const getDiscountMessage = useMemo(() => {
        const discountAmount = cartResponse?.bill?.dineoutPricingInfo?.totalDiscount;
        return discountAmount ? format(COLLAPSIBLE_BILL_DISCOUNT_MESSAGE, String(discountAmount)) : "";
    }, [cartResponse?.bill?.dineoutPricingInfo?.totalDiscount]);

    const [isBillDetailSectionUncollapsed, setBillDetailSectionUncollapsed] = useSessionStorage(
        SESSION_STORAGE_KEYS.IS_BILL_DETAIL_SECTION_UNCOLLAPSED,
        true,
    );

    const { totalBill = 0, billToPay = 0 } = useMemo(() => {
        const info = cartResponse?.bill?.dineoutPricingInfo;
        return {
            totalBill: info?.totalBill,
            billToPay: info?.billToPay,
        };
    }, [cartResponse?.bill?.dineoutPricingInfo]);

    const billDetailsProps: IBillDetailsRendererProps = useMemo(
        () => ({
            renderingDetails: [
                {
                    type: "title",
                    key: "bill_heading",
                    display_text: "Bill Details",
                    intermediateText: "",
                    value: "",
                    hierarchy: 1,
                    currency: "rupees",
                    info_text: "",
                    icon: "",
                    discount_message: "",
                    meta: {
                        height: "X",
                    },
                    children: renderingDetails,
                },
            ],
            planAddedRef: { current: null },
            planDetails: {
                plan_id: 0,
                type: "",
                program: "",
                name: "",
                base_price: 0,
                final_price: 0,
            },
            billDetailsRendererData: {
                displayData: {
                    totalSavings: 0,
                    discountType: "",
                    isInTooltip: false,
                    isOfferPack: false,
                },
                tooltipDisplayData: {
                    onImpressionDeliveryFeeLineItem,
                    onImpressionTaxesAndChargesLineItem,
                    onImpressionDeliveryFeeUnCollapsedLineItem,
                    freeDelCommsStyles: undefined,
                },
                titleData: {},
                collapsibleWidgetData: {
                    collapsedBillDetails: {
                        title: BILL_ELEMENT_ENTRIES.TO_PAY,
                        totalAmount: totalBill > billToPay ? String(totalBill) : "",
                        subtitle: "",
                        strikeoffAmount: String(billToPay),
                        discountMessage: getDiscountMessage,
                        savingsAmount: "",
                        upsellMessage: "",
                        upsellIcon: "",
                        icon: "",
                        discountMessageColor: "",
                        uncollapsed: false,
                        surgeApplied: false,
                    },
                    collapseBillDetailXPVariant: CollapsableBillDetailsVariants.VARIANT_WITH_SAVING_UPSELL,
                    isBillDetailSectionUncollapsed,
                    doNotShowCollapsableBill: false,
                    onImpressionBillViewChevron,
                    onClickBillViewChevron,
                    setBillDetailSectionUncollapsed,
                    onImpressionCollapsibleBillUpsell,
                },
            },
            billDetailsAnalytics: {
                onImpressionBanner,
                onClickBanner,
                onImpressionBannerHalfcard,
                onClickCloseBannerHalfcard,
            },
        }),
        [
            renderingDetails,
            totalBill,
            billToPay,
            getDiscountMessage,
            isBillDetailSectionUncollapsed,
            onImpressionBillViewChevron,
            onClickBillViewChevron,
            setBillDetailSectionUncollapsed,
            onImpressionCollapsibleBillUpsell,
            onImpressionDeliveryFeeLineItem,
            onImpressionTaxesAndChargesLineItem,
            onImpressionDeliveryFeeUnCollapsedLineItem,
            onImpressionBanner,
            onClickBanner,
            onImpressionBannerHalfcard,
            onClickCloseBannerHalfcard,
        ],
    );

    return {
        billDetailsProps,
    };
};

export default useDineoutCollapsibleBillDetails;
